// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addReorderPadFilters from "@keller/App/Handlers/ReorderPad/AddReorderPadFilters";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { getReorderPadDataViewProperty } from "@keller/App/Store/Selectors/ReorderPadSelectors";
import Pagination, { PaginationPresentationProps } from "@keller/MobiusOverrides/Pagination";
import { mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showResultsPerPage?: boolean;
    showPagesNumber?: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    pagination: getReorderPadDataViewProperty(state, "pagination"),
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    addReorderPadFilters,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                &&& {
                    margin: 0;
                    ${xScreen("lte", "md")} {
                        flex: 1;
                        width: 100%;
                    }
                }
            `,
            perPageSelect: css`
                ${xScreen("lte", "md")} {
                    margin: 0;
                }
            `,
        },
        selectProps: {
            labelProps: {
                size: 15,
                weight: "normal",
                css: css`
                    width: 120px;                        
                    text-align: left;
                    
                    ${xScreen("lte", "md")} {
                        width: 100%;
                        font-weight: bold;
                    },                    
                `,
            },
            cssOverrides: {
                formField: css`
                    width: 215px;
                    ${xScreen("lte", "md")} {
                        width: 100%;
                    }
                `,
                formInputWrapper: css`
                    flex-grow: 1;
                `,
            },
        },
    },
};

const styles = paginationStyles;

const ReorderPadPagination = ({
    addReorderPadFilters,
    pagination,
    showResultsPerPage,
    showPagesNumber,
    isTabletScreen,
}: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addReorderPadFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addReorderPadFilters({ pageSize: newPageSize });
    };

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const paginationStyle = showPagesNumber
        ? styles.pagination
        : mergeWithCss(styles.pagination, {
              cssOverrides: {
                  linkList: css`
                      &&&& {
                          display: none !important;
                      }
                  `,
              },
          } as typeof styles.pagination);

    return (
        <Pagination
            {...paginationStyle}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={showResultsPerPage ? pagination.pageSizeOptions : []}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            labelPosition={isTabletScreen ? "top" : "left"}
            pageSizeCookie="ReorderPadProductList-PageSize"
            data-test-selector="productList_pagination"
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadPagination);
